// collection-filters.js - FINAL VERSION with comma-splitting fix
class CollectionFilters {
  constructor() {
    this.activeFilters = {};
    this.isSidebarOpen = false;
    this.filterConfig = [];
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.cacheElements();
    this.parseFilterConfig();
    this.getFiltersFromURL();
    this.extractFilterValues();
    this.buildFilterOptions();
    this.bindEvents();
    this.applyFilters();
    this.updateFilterCount();
  }

  cacheElements() {
    this.collectionItems = Array.from(document.querySelectorAll('[data-collection-item]'));
    
    this.filterContainers = {};
    const filterGroups = document.querySelectorAll('[data-filter-group]');
    
    filterGroups.forEach(group => {
      const groupId = group.dataset.filterGroup;
      const container = group.querySelector('.collection-filters__options');
      if (container && container.id) {
        this.filterContainers[groupId] = container;
      }
    });

    Object.keys(this.filterContainers).forEach(groupId => {
      this.activeFilters[groupId] = [];
    });

    this.activeFiltersList = document.getElementById('ActiveFiltersList');
    this.filterResultsText = document.getElementById('FilterResultsCount');
    this.filterCount = document.querySelector('[data-filter-count]');
    this.clearAllBtn = document.getElementById('ClearAllFilters');
    this.filterToggle = document.querySelector('[data-filter-toggle]');
    this.filterSidebar = document.querySelector('[data-filter-sidebar-content]');
    this.filterOverlay = document.querySelector('[data-filter-overlay]');
    this.filterClose = document.querySelector('[data-filter-close]');
  }

  parseFilterConfig() {
    const configElement = document.querySelector('[data-filter-config]');
    if (configElement) {
      try {
        this.filterConfig = JSON.parse(configElement.textContent);
      } catch (e) {
        console.error('Failed to parse filter config:', e);
        this.filterConfig = [];
      }
    }
  }

  getFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    
    Object.keys(this.activeFilters).forEach(filterType => {
      const values = params.getAll(`filter-${filterType}`);
      if (values.length) {
        this.activeFilters[filterType] = values;
      }
    });
  }

  updateURL() {
    const params = new URLSearchParams();
    
    Object.keys(this.activeFilters).forEach(filterType => {
      this.activeFilters[filterType].forEach(value => {
        if (value && value !== 'unspecified') {
          params.append(`filter-${filterType}`, value);
        }
      });
    });
    
    const newURL = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    
    window.history.replaceState({}, '', newURL);
  }

  extractFilterValues() {
    this.filterValues = {};
    
    Object.keys(this.filterContainers).forEach(groupId => {
      this.filterValues[groupId] = new Set();
    });

    const allFilterValuesElement = document.querySelector('[data-all-filter-values]');
    if (allFilterValuesElement) {
      try {
        const allValues = JSON.parse(allFilterValuesElement.textContent);
        Object.keys(allValues).forEach(groupId => {
          if (this.filterValues[groupId]) {
            allValues[groupId].forEach(value => {
              if (value && value.includes(',')) {
                value.split(',').forEach(item => {
                  const trimmed = item.trim();
                  if (trimmed && trimmed !== 'unspecified') {
                    this.filterValues[groupId].add(trimmed);
                  }
                });
              } else if (value && value !== 'unspecified') {
                this.filterValues[groupId].add(value);
              }
            });
          }
        });
        return;
      } catch (e) {
        console.error('Failed to parse all filter values:', e);
      }
    }

    this.collectionItems.forEach(item => {
      Object.keys(this.filterContainers).forEach(groupId => {
        const dataAttrName = 'filter' + this.capitalizeFirst(groupId.replace('-', ' ')).replace(/\s+/g, '');
        const rawValue = item.dataset[dataAttrName];
        
        if (rawValue && rawValue !== 'unspecified') {
          if (rawValue.includes(',')) {
            const items = rawValue.split(',')
              .map(item => item.trim())
              .filter(item => item && item !== 'unspecified');
            
            items.forEach(item => {
              this.filterValues[groupId].add(item);
            });
          } else {
            this.filterValues[groupId].add(rawValue);
          }
        }
      });
    });
  }

  buildFilterOptions() {
    Object.keys(this.filterContainers).forEach(groupId => {
      const container = this.filterContainers[groupId];
      if (!container) return;

      container.innerHTML = '';

      const valuesSet = this.filterValues[groupId] || new Set();
      const values = Array.from(valuesSet)
        .filter(value => value && value !== 'unspecified')
        .sort((a, b) => a.localeCompare(b));

      if (valuesSet.has('unspecified')) {
        values.unshift('unspecified');
      }

      if (values.length === 0) {
        const placeholder = document.createElement('p');
        placeholder.className = 'no-filter-values';
        placeholder.textContent = 'No values found';
        placeholder.style.fontSize = '14px';
        placeholder.style.color = 'rgba(var(--color-foreground), 0.5)';
        placeholder.style.fontStyle = 'italic';
        container.appendChild(placeholder);
        return;
      }

      values.forEach(value => {
        const displayValue = this.formatDisplayValue(value);
        const optionId = `${groupId}-${value.replace(/\s+/g, '-').toLowerCase()}`;

        const optionDiv = document.createElement('div');
        optionDiv.className = 'filter-option';
        optionDiv.dataset.filterValue = value;
        optionDiv.dataset.filterType = groupId;

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = optionId;
        checkbox.value = value;
        checkbox.dataset.filterType = groupId;
        checkbox.className = 'filter-checkbox';
        
        checkbox.style.cssText = `
          width: 18px;
          height: 18px;
          border: 1px solid #999;
          border-radius: 3px;
          cursor: pointer;
          background-color: white;
          display: block;
          flex-shrink: 0;
          margin: 0;
          appearance: none;
          -webkit-appearance: none;
          position: relative;
        `;
        
        if (this.activeFilters[groupId] && this.activeFilters[groupId].includes(value)) {
          checkbox.checked = true;
          checkbox.style.backgroundColor = '#000';
          checkbox.style.borderColor = '#000';
        }

        const label = document.createElement('label');
        label.htmlFor = optionId;
        label.className = 'filter-option-label';
        label.style.display = 'flex';
        label.style.justifyContent = 'space-between';
        label.style.alignItems = 'center';
        label.style.flex = '1';
        label.style.cursor = 'pointer';
        label.style.padding = '6px 0';
        
        const textSpan = document.createElement('span');
        textSpan.className = 'filter-option-text';
        textSpan.textContent = displayValue;
        textSpan.style.fontSize = '14px';
        textSpan.style.color = 'rgb(var(--color-foreground))';
        
        const countSpan = document.createElement('span');
        countSpan.className = 'filter-option-count';
        countSpan.textContent = '0';
        countSpan.style.fontSize = '13px';
        countSpan.style.color = 'rgba(var(--color-foreground), 0.6)';
        countSpan.style.marginLeft = '8px';

        label.appendChild(textSpan);
        label.appendChild(countSpan);
        
        optionDiv.appendChild(checkbox);
        optionDiv.appendChild(label);
        container.appendChild(optionDiv);
      });
    });

    this.updateOptionCounts();
    this.addCheckboxStyles();
  }

  addCheckboxStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .filter-checkbox:checked::after {
        content: '✓';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: white;
        font-size: 12px;
        font-weight: bold;
      }
      
      .filter-checkbox:hover {
        border-color: #666;
      }
      
      .filter-checkbox:focus {
        outline: 2px solid #0066cc;
        outline-offset: 1px;
      }
    `;
    document.head.appendChild(style);
  }

  updateOptionCounts() {
    if (!this.collectionItems.length) return;

    const counts = {};
    Object.keys(this.filterContainers).forEach(groupId => {
      counts[groupId] = {};
      const valuesSet = this.filterValues[groupId] || new Set();
      valuesSet.forEach(value => {
        counts[groupId][value] = 0;
      });
    });

    this.collectionItems.forEach(item => {
      if (this.isItemVisibleIgnoringCurrentFilter(item)) {
        Object.keys(counts).forEach(groupId => {
          const dataAttrName = 'filter' + this.capitalizeFirst(groupId.replace('-', ' ')).replace(/\s+/g, '');
          const rawValue = item.dataset[dataAttrName];
          
          if (rawValue && rawValue !== 'unspecified') {
            if (rawValue.includes(',')) {
              const values = rawValue.split(',').map(v => v.trim()).filter(v => v);
              values.forEach(value => {
                if (counts[groupId][value] !== undefined) {
                  counts[groupId][value]++;
                }
              });
            } else if (counts[groupId][rawValue] !== undefined) {
              counts[groupId][rawValue]++;
            }
          }
        });
      }
    });

    Object.keys(this.filterContainers).forEach(groupId => {
      const container = this.filterContainers[groupId];
      if (!container) return;

      const options = container.querySelectorAll('.filter-option');
      options.forEach(option => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        if (!checkbox) return;
        
        const value = checkbox.value;
        const count = counts[groupId][value] || 0;
        const countSpan = option.querySelector('.filter-option-count');
        
        if (countSpan) {
          countSpan.textContent = `(${count})`;
          
          if (count === 0 && !checkbox.checked) {
            const label = option.querySelector('label');
            if (label) {
              label.style.opacity = '0.5';
              label.style.color = 'rgba(var(--color-foreground), 0.5)';
            }
          } else {
            const label = option.querySelector('label');
            if (label) {
              label.style.opacity = '1';
              label.style.color = '';
            }
          }
        }
      });
    });
  }

  isItemVisibleIgnoringCurrentFilter(item) {
    for (const filterType in this.activeFilters) {
      const activeValues = this.activeFilters[filterType];
      if (activeValues.length > 0) {
        const dataAttrName = 'filter' + this.capitalizeFirst(filterType.replace('-', ' ')).replace(/\s+/g, '');
        const itemRawValue = item.dataset[dataAttrName];
        
        if (!itemRawValue || itemRawValue === 'unspecified') {
          if (!activeValues.includes('unspecified')) {
            return false;
          }
          continue;
        }
        
        const itemValues = itemRawValue.includes(',') 
          ? itemRawValue.split(',').map(v => v.trim()).filter(v => v)
          : [itemRawValue];
        
        let hasMatch = false;
        for (const selectedValue of activeValues) {
          if (itemValues.includes(selectedValue)) {
            hasMatch = true;
            break;
          }
        }
        
        if (!hasMatch) {
          return false;
        }
      }
    }
    return true;
  }

  bindEvents() {
    Object.values(this.filterContainers).forEach(container => {
      if (container) {
        container.addEventListener('change', (e) => {
          if (e.target.type === 'checkbox') {
            this.handleFilterChange(e.target);
          }
        });
      }
    });

    if (this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }

    if (this.filterToggle) {
      this.filterToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSidebar();
      });
    }

    if (this.filterClose) {
      this.filterClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeSidebar();
      });
    }

    if (this.filterOverlay) {
      this.filterOverlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeSidebar();
      });
    }

    document.querySelectorAll('[data-filter-group-toggle]').forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleFilterGroup(toggle);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isSidebarOpen) {
        this.closeSidebar();
      }
    });

    if (this.activeFiltersList) {
      this.activeFiltersList.addEventListener('click', (e) => {
        if (e.target.classList.contains('active-filter__remove')) {
          e.preventDefault();
          const filterType = e.target.dataset.filterType;
          const value = e.target.dataset.filterValue;
          this.removeFilter(filterType, value);
        }
      });
    }
  }

  toggleSidebar() {
    if (this.isSidebarOpen) {
      this.closeSidebar();
    } else {
      this.openSidebar();
    }
  }

  openSidebar() {
    this.isSidebarOpen = true;
    
    if (this.filterToggle) {
      this.filterToggle.setAttribute('aria-expanded', 'true');
    }
    
    if (this.filterSidebar) {
      this.filterSidebar.classList.add('active');
    }
    
    if (this.filterOverlay) {
      this.filterOverlay.classList.add('active');
    }
    
    document.body.classList.add('filter-sidebar-open');
  }

  closeSidebar() {
    this.isSidebarOpen = false;
    
    if (this.filterToggle) {
      this.filterToggle.setAttribute('aria-expanded', 'false');
    }
    
    if (this.filterSidebar) {
      this.filterSidebar.classList.remove('active');
    }
    
    if (this.filterOverlay) {
      this.filterOverlay.classList.remove('active');
    }
    
    document.body.classList.remove('filter-sidebar-open');
  }

  toggleFilterGroup(toggle) {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', !isExpanded);
  }

  handleFilterChange(checkbox) {
    const filterType = checkbox.dataset.filterType;
    const value = checkbox.value;

    if (!this.activeFilters[filterType]) {
      this.activeFilters[filterType] = [];
    }

    if (checkbox.checked) {
      if (!this.activeFilters[filterType].includes(value)) {
        this.activeFilters[filterType].push(value);
      }
      checkbox.style.backgroundColor = '#000';
      checkbox.style.borderColor = '#000';
    } else {
      this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== value);
      checkbox.style.backgroundColor = 'white';
      checkbox.style.borderColor = '#999';
    }

    this.updateFilterCount();
    this.updateActiveFilters();
    this.applyFilters();
    this.updateURL();
  }

  updateFilterCount() {
    if (!this.filterCount) {
      this.filterCount = document.querySelector('[data-filter-count]');
      if (!this.filterCount) return;
    }
    
    let totalActive = 0;
    Object.values(this.activeFilters).forEach(arr => {
      totalActive += arr.length;
    });
    
    this.filterCount.textContent = totalActive;
    this.filterCount.setAttribute('data-filter-count', totalActive);
    
    if (totalActive > 0) {
      this.filterCount.style.display = 'inline-block';
    } else {
      this.filterCount.style.display = 'none';
    }
  }

  matchesFilters(item) {
    for (const filterType in this.activeFilters) {
      const activeValues = this.activeFilters[filterType];
      
      if (activeValues.length > 0) {
        const dataAttrName = 'filter' + this.capitalizeFirst(filterType.replace('-', ' ')).replace(/\s+/g, '');
        const itemRawValue = item.dataset[dataAttrName];
        
        if (!itemRawValue || itemRawValue === 'unspecified') {
          if (!activeValues.includes('unspecified')) {
            return false;
          }
          continue;
        }
        
        const itemValues = itemRawValue.includes(',') 
          ? itemRawValue.split(',').map(v => v.trim()).filter(v => v)
          : [itemRawValue];
        
        let hasMatch = false;
        
        for (const selectedValue of activeValues) {
          if (itemValues.includes(selectedValue)) {
            hasMatch = true;
            break;
          }
        }
        
        if (!hasMatch) {
          return false;
        }
      }
    }
    
    return true;
  }

  isItemVisible(item) {
    return this.matchesFilters(item);
  }

  applyFilters() {
    let visibleCount = 0;

    this.collectionItems.forEach(item => {
      if (this.isItemVisible(item)) {
        item.style.display = '';
        item.classList.remove('filter-hidden');
        visibleCount++;
      } else {
        item.style.display = 'none';
        item.classList.add('filter-hidden');
      }
    });

    this.updateResults(visibleCount);
    this.updateOptionCounts();
    this.updateActiveFilters();
  }

  updateResults(visibleCount) {
    if (!this.filterResultsText) return;
    
    const total = this.collectionItems.length;
    if (visibleCount === undefined) {
      visibleCount = total;
    }
    
    let resultsText = `${visibleCount} collection${visibleCount !== 1 ? 's' : ''}`;
    
    if (visibleCount < total) {
      resultsText = `${visibleCount} of ${total} collections`;
    }
    
    this.filterResultsText.textContent = resultsText;
  }

  updateActiveFilters() {
    if (!this.activeFiltersList) return;

    this.activeFiltersList.innerHTML = '';

    Object.keys(this.activeFilters).forEach(filterType => {
      const filterConfig = this.filterConfig.find(f => f.id === filterType);
      const displayName = filterConfig ? filterConfig.label : this.formatDisplayValue(filterType);
      
      this.activeFilters[filterType].forEach(value => {
        if (value && value !== 'unspecified') {
          const tag = document.createElement('div');
          tag.className = 'active-filter-tag';

          const displayValue = this.formatDisplayValue(value);

          tag.innerHTML = `
            <span>${displayName}: ${displayValue}</span>
            <button type="button" class="active-filter__remove" 
                    data-filter-type="${filterType}" 
                    data-filter-value="${value}"
                    aria-label="Remove ${displayValue} filter">
              ×
            </button>
          `;

          this.activeFiltersList.appendChild(tag);
        }
      });
    });
  }

  removeFilter(filterType, value) {
    if (!this.activeFilters[filterType]) return;
    
    this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== value);

    const checkbox = document.querySelector(`input[data-filter-type="${filterType}"][value="${value}"]`);
    if (checkbox) {
      checkbox.checked = false;
      checkbox.style.backgroundColor = 'white';
      checkbox.style.borderColor = '#999';
    }

    this.updateFilterCount();
    this.updateActiveFilters();
    this.applyFilters();
    this.updateURL();
  }

  clearAllFilters() {
    Object.keys(this.activeFilters).forEach(filterType => {
      this.activeFilters[filterType] = [];
    });

    document.querySelectorAll('input[type="checkbox"][data-filter-type]').forEach(checkbox => {
      checkbox.checked = false;
      checkbox.style.backgroundColor = 'white';
      checkbox.style.borderColor = '#999';
    });

    this.updateFilterCount();
    this.updateActiveFilters();
    this.applyFilters();
    this.updateURL();
    
    if (window.innerWidth <= 768) {
      this.closeSidebar();
    }
  }

  formatDisplayValue(value) {
    if (value === 'unspecified') return 'Unspecified';
    return value.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  try {
    if (document.getElementById('CollectionFilters')) {
      window.collectionFilters = new CollectionFilters();
    }
  } catch (error) {
    console.error('Error initializing CollectionFilters:', error);
  }
});